import React, { useState, useEffect, useCallback } from 'react';
import { 
    Calendar, Clock, MapPin, Users, Plus, Check, X, HelpCircle,
    Loader2, Send, Trash2, Edit2, CalendarPlus, UserPlus, 
    CheckCircle2, AlertCircle, UserMinus
} from 'lucide-react';
import { apiFetch } from '../utils/api';
import { fetchClubBetaFeatures, isBetaEnabled } from '../utils/betaFeatures';
import { useI18n } from '../i18n';
import { formatMemberText } from '../locales/member';

/**
 * EventScheduler - Simplified game night scheduling
 * One time per event (mandatory), no time slot voting
 */
const EventScheduler = ({ user, games, players }) => {
    const { t, language } = useI18n(user?.club?.language);
    const dateLocale = language === 'sv' ? 'sv-SE' : language === 'en' ? 'en-GB' : 'fi-FI';
    const [events, setEvents] = useState([]);
    const [allPlayers, setAllPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [featureEnabled, setFeatureEnabled] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [gameNightsAdminOnly, setGameNightsAdminOnly] = useState(false);

    // Fetch events and settings
    const fetchData = useCallback(async () => {
        try {
            const beta = await fetchClubBetaFeatures();
            const enabled = isBetaEnabled(beta, 'game_nights');
            setFeatureEnabled(enabled);
            if (!enabled) {
                setLoading(false);
                return;
            }

            const [eventsRes, featuresRes, playersRes] = await Promise.all([
                apiFetch('/api/events?upcoming=true'),
                apiFetch('/api/club/features'),
                apiFetch('/api/players')
            ]);
            
            if (eventsRes.ok) setEvents(await eventsRes.json());
            if (featuresRes.ok) {
                const features = await featuresRes.json();
                setGameNightsAdminOnly(features.game_nights_admin_only || false);
            }
            if (playersRes.ok) {
                const playersData = await playersRes.json();
                // Filter active players (isActive can be true/1 or "active")
                setAllPlayers(playersData.filter(p => p.isActive === true || p.isActive === 1));
            }
        } catch (err) {
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);
    
    // Check if user can create events
    const canCreateEvent = !gameNightsAdminOnly || user?.role === 'admin' || user?.role === 'superadmin';
    const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

    // Fetch single event details
    const fetchEventDetails = async (eventId) => {
        try {
            const res = await apiFetch(`/api/events/${eventId}`);
            if (res.ok) {
                const data = await res.json();
                setSelectedEvent(data);
            }
        } catch (err) {
            console.error('Event fetch error:', err);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const options = { weekday: 'short', day: 'numeric', month: 'short' };
        return date.toLocaleDateString(dateLocale, options);
    };

    const formatTime = (timeStr) => {
        if (!timeStr) return '';
        // Handle both ISO date strings (1970-01-01T18:00:00.000Z) and time strings (18:00:00)
        if (typeof timeStr === 'string') {
            // If it contains 'T', it's an ISO date string - extract time part
            if (timeStr.includes('T')) {
                const timePart = timeStr.split('T')[1];
                return timePart ? timePart.substring(0, 5) : '';
            }
            // Otherwise just take first 5 chars (HH:MM)
            return timeStr.substring(0, 5);
        }
        // Handle Date objects
        if (timeStr instanceof Date) {
            return timeStr.toTimeString().substring(0, 5);
        }
        return '';
    };

    if (loading || featureEnabled === null) {
        return (
            <div className="event-scheduler-loading">
                <Loader2 size={32} className="spin" />
                <span>{t.eventScheduler.loading}</span>
            </div>
        );
    }

    if (!featureEnabled) {
        return (
            <div className="event-scheduler" style={{ padding: '1.5rem' }}>
                <h2><Calendar size={24} /> {t.eventScheduler.title}</h2>
                <p style={{ color: '#7f8c8d', marginTop: '1rem' }}>
                    {t.eventScheduler.featureDisabled}
                </p>
            </div>
        );
    }

    return (
        <div className="event-scheduler">
            <div className="scheduler-header">
                <h2><Calendar size={24} /> {t.eventScheduler.title}</h2>
                {canCreateEvent && (
                    <button 
                        className="btn-primary"
                        onClick={() => setShowCreateForm(true)}
                    >
                        <CalendarPlus size={18} /> {t.eventScheduler.suggestEvent}
                    </button>
                )}
            </div>

            {/* Events List */}
            <div className="events-grid">
                {events.length === 0 ? (
                    <div className="no-events">
                        <Calendar size={48} />
                        <p>{t.eventScheduler.noUpcoming}</p>
                        {canCreateEvent && (
                            <button 
                                className="btn-secondary"
                                onClick={() => setShowCreateForm(true)}
                            >
                                {t.eventScheduler.suggestFirst}
                            </button>
                        )}
                    </div>
                ) : (
                    events.map(event => (
                        <EventCard 
                            key={event.id}
                            event={event}
                            onClick={() => fetchEventDetails(event.id)}
                            formatDate={formatDate}
                            formatTime={formatTime}
                            t={t}
                        />
                    ))
                )}
            </div>

            {/* Event Details Modal */}
            {selectedEvent && (
                <EventDetailsModal
                    event={selectedEvent}
                    user={user}
                    isAdmin={isAdmin}
                    allPlayers={allPlayers}
                    onClose={() => setSelectedEvent(null)}
                    onRespond={async (status) => {
                        await apiFetch(`/api/events/${selectedEvent.id}/respond`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ playerId: user.id, status })
                        });
                        fetchEventDetails(selectedEvent.id);
                    }}
                    onDelete={async () => {
                        if (window.confirm(t.eventScheduler.confirmDeleteEvent)) {
                            await apiFetch(`/api/events/${selectedEvent.id}`, { method: 'DELETE' });
                            setSelectedEvent(null);
                            fetchData();
                        }
                    }}
                    onUpdateParticipant={async (playerId, status) => {
                        await apiFetch(`/api/events/${selectedEvent.id}/participants/${playerId}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ status })
                        });
                        fetchEventDetails(selectedEvent.id);
                    }}
                    onRemoveParticipant={async (playerId) => {
                        await apiFetch(`/api/events/${selectedEvent.id}/participants/${playerId}`, {
                            method: 'DELETE'
                        });
                        fetchEventDetails(selectedEvent.id);
                    }}
                    onAddParticipant={async (playerId, status) => {
                        await apiFetch(`/api/events/${selectedEvent.id}/participants/${playerId}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ status })
                        });
                        fetchEventDetails(selectedEvent.id);
                    }}
                    formatDate={formatDate}
                    formatTime={formatTime}
                />
            )}

            {/* Create Event Modal */}
            {showCreateForm && (
                <CreateEventModal
                    user={user}
                    games={games}
                    onClose={() => setShowCreateForm(false)}
                    onCreated={() => {
                        setShowCreateForm(false);
                        fetchData();
                    }}
                />
            )}

            <style>{`
                .event-scheduler {
                    padding: 1.5rem;
                    max-width: 1200px;
                    margin: 0 auto;
                }

                .scheduler-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.5rem;
                }

                .scheduler-header h2 {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    color: #f0f6fc;
                    margin: 0;
                }

                .event-scheduler-loading {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 1rem;
                    padding: 3rem;
                    color: #8b949e;
                }

                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { to { transform: rotate(360deg); } }

                .events-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
                    gap: 1rem;
                }

                .no-events {
                    grid-column: 1 / -1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 1rem;
                    padding: 3rem;
                    background: #161b22;
                    border: 2px dashed #30363d;
                    border-radius: 12px;
                    color: #8b949e;
                }

                /* Event Card */
                .event-card {
                    background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
                    border: 1px solid #374151;
                    border-radius: 12px;
                    padding: 1.25rem;
                    cursor: pointer;
                    transition: all 0.2s;
                    border-left: 4px solid #22c55e;
                }

                .event-card:hover {
                    border-color: #60a5fa;
                    transform: translateY(-2px);
                }

                .event-card.cancelled {
                    border-left-color: #ef4444;
                    opacity: 0.7;
                }

                .event-card-header {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.75rem;
                    margin-bottom: 0.75rem;
                }

                .event-card-image {
                    width: 48px;
                    height: 48px;
                    border-radius: 8px;
                    object-fit: cover;
                    background: #374151;
                }

                .event-card-title {
                    flex: 1;
                }

                .event-card-title h3 {
                    margin: 0 0 0.25rem 0;
                    color: #f0f6fc;
                    font-size: 1rem;
                }

                .event-card-game {
                    color: #60a5fa;
                    font-size: 0.85rem;
                }

                .event-card-datetime {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    background: rgba(34, 197, 94, 0.15);
                    color: #22c55e;
                    padding: 0.5rem 0.75rem;
                    border-radius: 8px;
                    font-weight: 600;
                    margin-bottom: 0.75rem;
                }

                .event-card-meta {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.75rem;
                    font-size: 0.8rem;
                    color: #9ca3af;
                }

                .event-card-meta span {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                }

                /* Modal Base */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.8);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 10000;
                    padding: 1rem;
                }

                .modal-content {
                    background: #161b22;
                    border: 1px solid #30363d;
                    border-radius: 16px;
                    max-width: 600px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1.25rem;
                    border-bottom: 1px solid #30363d;
                    position: sticky;
                    top: 0;
                    background: #161b22;
                }

                .modal-header h3 {
                    margin: 0;
                    color: #f0f6fc;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .modal-close {
                    background: rgba(255, 255, 255, 0.1);
                    border: none;
                    color: #9ca3af;
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .modal-close:hover {
                    background: rgba(255, 255, 255, 0.2);
                    color: #fff;
                }

                .modal-body {
                    padding: 1.25rem;
                }

                /* Vote/Response Buttons */
                .vote-buttons {
                    display: flex;
                    gap: 0.5rem;
                }

                .vote-btn {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.25rem;
                    padding: 0.6rem;
                    border-radius: 8px;
                    border: 2px solid transparent;
                    cursor: pointer;
                    font-size: 0.85rem;
                    font-weight: 500;
                    transition: all 0.2s;
                }

                .vote-btn.yes {
                    background: rgba(34, 197, 94, 0.1);
                    border-color: #22c55e;
                    color: #22c55e;
                }

                .vote-btn.yes:hover, .vote-btn.yes.active {
                    background: #22c55e;
                    color: #000;
                }

                .vote-btn.maybe {
                    background: rgba(245, 158, 11, 0.1);
                    border-color: #f59e0b;
                    color: #f59e0b;
                }

                .vote-btn.maybe:hover, .vote-btn.maybe.active {
                    background: #f59e0b;
                    color: #000;
                }

                .vote-btn.no {
                    background: rgba(239, 68, 68, 0.1);
                    border-color: #ef4444;
                    color: #ef4444;
                }

                .vote-btn.no:hover, .vote-btn.no.active {
                    background: #ef4444;
                    color: #fff;
                }

                /* Form */
                .form-group {
                    margin-bottom: 1rem;
                }

                .form-group label {
                    display: block;
                    margin-bottom: 0.5rem;
                    color: #c9d1d9;
                    font-size: 0.9rem;
                }

                .form-group input,
                .form-group select,
                .form-group textarea {
                    width: 100%;
                    padding: 0.75rem;
                    background: #0d1117;
                    border: 1px solid #30363d;
                    border-radius: 8px;
                    color: #c9d1d9;
                    font-size: 0.9rem;
                }

                .form-group input:focus,
                .form-group select:focus,
                .form-group textarea:focus {
                    outline: none;
                    border-color: #58a6ff;
                }

                .form-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1rem;
                }

                .modal-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 0.75rem;
                    padding: 1.25rem;
                    border-top: 1px solid #30363d;
                }

                .btn-primary {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.25rem;
                    background: #238636;
                    border: none;
                    border-radius: 8px;
                    color: #fff;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.2s;
                }

                .btn-primary:hover {
                    background: #2ea043;
                }

                .btn-secondary {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.25rem;
                    background: #21262d;
                    border: 1px solid #30363d;
                    border-radius: 8px;
                    color: #c9d1d9;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .btn-secondary:hover {
                    background: #30363d;
                }

                .btn-danger {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 1rem;
                    background: rgba(239, 68, 68, 0.1);
                    border: 1px solid #ef4444;
                    border-radius: 8px;
                    color: #ef4444;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .btn-danger:hover {
                    background: #ef4444;
                    color: #fff;
                }

                /* Admin controls */
                .admin-controls {
                    display: flex;
                    gap: 0.5rem;
                    margin-top: 1rem;
                    padding-top: 1rem;
                    border-top: 1px solid #30363d;
                }

                /* Participant list */
                .participant-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0.5rem;
                    border-radius: 8px;
                    background: rgba(0,0,0,0.2);
                    margin-bottom: 0.5rem;
                }

                .participant-info {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .participant-actions {
                    display: flex;
                    gap: 0.25rem;
                }

                .participant-actions button {
                    padding: 0.25rem 0.5rem;
                    border-radius: 4px;
                    border: none;
                    cursor: pointer;
                    font-size: 0.75rem;
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .event-scheduler {
                        padding: 1rem;
                    }

                    .scheduler-header {
                        flex-direction: column;
                        gap: 1rem;
                        align-items: stretch;
                    }

                    .events-grid {
                        grid-template-columns: 1fr;
                    }

                    .form-row {
                        grid-template-columns: 1fr;
                    }

                    .vote-buttons {
                        flex-direction: column;
                    }
                }
            `}</style>
        </div>
    );
};

// Event Card Component - simplified with event time displayed prominently
const EventCard = ({ event, onClick, formatDate, formatTime, t }) => (
    <div className={`event-card ${event.status === 'cancelled' ? 'cancelled' : ''}`} onClick={onClick}>
        <div className="event-card-header">
            {event.gameImage && (
                <img src={event.gameImage} alt="" className="event-card-image" />
            )}
            <div className="event-card-title">
                <h3>{event.title}</h3>
                {event.gameName && <span className="event-card-game">{event.gameName}</span>}
            </div>
        </div>
        
        {/* Event time - prominently displayed */}
        {event.eventDate && (
            <div className="event-card-datetime">
                <Calendar size={16} />
                {formatDate(event.eventDate)}
                {event.eventTime && (
                    <>
                        <Clock size={14} style={{ marginLeft: '0.5rem' }} />
                        {t.eventScheduler.atTime} {formatTime(event.eventTime)}
                    </>
                )}
            </div>
        )}
        
        <div className="event-card-meta">
            <span><Users size={14} /> {formatMemberText(t.eventScheduler.attendees, { count: event.attendeeCount || 0 })}</span>
            {event.locationName && (
                <span><MapPin size={14} /> {event.locationName}</span>
            )}
        </div>
    </div>
);

// Event Details Modal - simplified without time slot voting
const EventDetailsModal = ({ 
    event, user, isAdmin, allPlayers, onClose, onRespond, onDelete, 
    onUpdateParticipant, onRemoveParticipant, onAddParticipant, formatDate, formatTime 
}) => {
    const { t } = useI18n(user?.club?.language);
    const userParticipant = event.participants?.find(p => p.playerId === user.id);
    const isCreator = event.created_by === user.id;
    const canDelete = isCreator || isAdmin;
    const [showAddPlayer, setShowAddPlayer] = useState(false);
    
    // Players not yet in participants
    const availablePlayers = allPlayers.filter(
        p => !event.participants?.find(ep => ep.playerId === p.id)
    );

    // Group participants by status
    const attendingParticipants = event.participants?.filter(p => p.status === 'attending') || [];
    const maybeParticipants = event.participants?.filter(p => p.status === 'maybe') || [];
    const declinedParticipants = event.participants?.filter(p => p.status === 'declined') || [];

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>
                        <Calendar size={20} />
                        {event.title}
                    </h3>
                    <button className="modal-close" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>
                
                <div className="modal-body">
                    {/* Event Info */}
                    {event.gameName && (
                        <p style={{ color: '#60a5fa', marginBottom: '1rem' }}>
                            🎲 {event.gameName}
                        </p>
                    )}
                    
                    {event.description && (
                        <p style={{ color: '#9ca3af', marginBottom: '1rem' }}>
                            {event.description}
                        </p>
                    )}

                    {/* Event Info Box - Date, Time, Location */}
                    <div style={{ 
                        background: 'rgba(34, 197, 94, 0.1)',
                        border: '1px solid rgba(34, 197, 94, 0.25)',
                        borderRadius: '12px',
                        padding: '1.25rem',
                        marginBottom: '1.5rem'
                    }}>
                        {/* Date & Time */}
                        {event.eventDate && (
                            <div style={{ 
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.75rem',
                                marginBottom: event.locationName ? '1rem' : '0.5rem'
                            }}>
                                <div style={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    color: '#22c55e',
                                    fontWeight: 600,
                                    fontSize: '1.1rem'
                                }}>
                                    <Calendar size={20} />
                                    {formatDate(event.eventDate)}
                                </div>
                                {event.eventTime && (
                                    <>
                                        <span style={{ color: '#4b5563' }}>•</span>
                                        <div style={{ 
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.35rem',
                                            color: '#22c55e',
                                            fontWeight: 600,
                                            fontSize: '1.1rem'
                                        }}>
                                            <Clock size={18} />
                                            {formatTime(event.eventTime)}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                        
                        {/* Duration */}
                        {event.durationMinutes && (
                            <div style={{ 
                                textAlign: 'center',
                                fontSize: '0.85rem', 
                                color: '#9ca3af',
                                marginBottom: event.locationName ? '1rem' : 0
                            }}>
                                {formatMemberText(t.eventScheduler.durationApprox, { hours: Math.round(event.durationMinutes / 60) })}
                            </div>
                        )}
                        
                        {/* Location */}
                        {event.locationName && (
                            <div style={{ 
                                borderTop: '1px solid rgba(107, 114, 128, 0.3)',
                                paddingTop: '1rem',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '0.35rem'
                            }}>
                                <div style={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    color: '#a855f7',
                                    fontWeight: 600
                                }}>
                                    <MapPin size={18} />
                                    {event.locationName}
                                </div>
                                {event.locationAddress && (
                                    <div style={{ 
                                        fontSize: '0.85rem', 
                                        color: '#9ca3af',
                                        textAlign: 'center'
                                    }}>
                                        {event.locationAddress}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Your Response */}
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ color: '#9ca3af', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>
                            {t.eventScheduler.attendingQuestion}
                        </label>
                        <div className="vote-buttons">
                            <button 
                                className={`vote-btn yes ${userParticipant?.status === 'attending' ? 'active' : ''}`}
                                onClick={() => onRespond('attending')}
                            >
                                <Check size={16} /> {t.eventScheduler.attending}
                            </button>
                            <button 
                                className={`vote-btn maybe ${userParticipant?.status === 'maybe' ? 'active' : ''}`}
                                onClick={() => onRespond('maybe')}
                            >
                                <HelpCircle size={16} /> {t.eventScheduler.maybe}
                            </button>
                            <button 
                                className={`vote-btn no ${userParticipant?.status === 'declined' ? 'active' : ''}`}
                                onClick={() => onRespond('declined')}
                            >
                                <X size={16} /> {t.eventScheduler.notAttending}
                            </button>
                        </div>
                    </div>

                    {/* Participants List */}
                    <div style={{ 
                        background: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        padding: '1rem'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '0.75rem'
                        }}>
                            <label style={{ 
                                color: '#9ca3af', 
                                fontSize: '0.85rem', 
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <Users size={16} /> {t.eventScheduler.registrations}
                            </label>
                            {isAdmin && (
                                <button 
                                    className="btn-secondary"
                                    style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}
                                    onClick={() => setShowAddPlayer(!showAddPlayer)}
                                >
                                    <UserPlus size={14} /> {t.eventScheduler.addPlayer}
                                </button>
                            )}
                        </div>
                        
                        {/* Add Player dropdown */}
                        {showAddPlayer && isAdmin && availablePlayers.length > 0 && (
                            <div style={{ marginBottom: '1rem' }}>
                                <select 
                                    onChange={(e) => {
                                        if (e.target.value) {
                                            onAddParticipant(parseInt(e.target.value), 'attending');
                                            setShowAddPlayer(false);
                                        }
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        background: '#0d1117',
                                        border: '1px solid #30363d',
                                        borderRadius: '6px',
                                        color: '#c9d1d9'
                                    }}
                                >
                                    <option value="">{t.eventScheduler.selectPlayer}</option>
                                    {availablePlayers.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {/* Attending */}
                            {attendingParticipants.length > 0 && (
                                <div>
                                    <div style={{ 
                                        color: '#22c55e', 
                                        fontSize: '0.8rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.25rem',
                                        marginBottom: '0.25rem'
                                    }}>
                                        <Check size={14} /> {formatMemberText(t.eventScheduler.attendingList, { count: attendingParticipants.length })}
                                    </div>
                                    {attendingParticipants.map(p => (
                                        <div key={p.playerId} className="participant-item">
                                            <span className="participant-info" style={{ color: '#22c55e' }}>
                                                {p.playerName}
                                            </span>
                                            {isAdmin && p.playerId !== user.id && (
                                                <div className="participant-actions">
                                                    <button 
                                                        style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' }}
                                                        onClick={() => onRemoveParticipant(p.playerId)}
                                                        title={t.common.delete}
                                                    >
                                                        <UserMinus size={12} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {/* Maybe */}
                            {maybeParticipants.length > 0 && (
                                <div>
                                    <div style={{ 
                                        color: '#f59e0b', 
                                        fontSize: '0.8rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.25rem',
                                        marginBottom: '0.25rem'
                                    }}>
                                        <HelpCircle size={14} /> {formatMemberText(t.eventScheduler.maybeList, { count: maybeParticipants.length })}
                                    </div>
                                    {maybeParticipants.map(p => (
                                        <div key={p.playerId} className="participant-item">
                                            <span className="participant-info" style={{ color: '#f59e0b' }}>
                                                {p.playerName}
                                            </span>
                                            {isAdmin && (
                                                <div className="participant-actions">
                                                    <button 
                                                        style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }}
                                                        onClick={() => onUpdateParticipant(p.playerId, 'attending')}
                                                        title={t.eventScheduler.markAttending}
                                                    >
                                                        <Check size={12} />
                                                    </button>
                                                    <button 
                                                        style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' }}
                                                        onClick={() => onRemoveParticipant(p.playerId)}
                                                        title={t.common.delete}
                                                    >
                                                        <UserMinus size={12} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {/* Declined */}
                            {declinedParticipants.length > 0 && (
                                <div>
                                    <div style={{ 
                                        color: '#ef4444', 
                                        fontSize: '0.8rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.25rem',
                                        marginBottom: '0.25rem'
                                    }}>
                                        <X size={14} /> {formatMemberText(t.eventScheduler.declinedList, { count: declinedParticipants.length })}
                                    </div>
                                    {declinedParticipants.map(p => (
                                        <div key={p.playerId} className="participant-item">
                                            <span className="participant-info" style={{ color: '#ef4444' }}>
                                                {p.playerName}
                                            </span>
                                            {isAdmin && (
                                                <div className="participant-actions">
                                                    <button 
                                                        style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }}
                                                        onClick={() => onUpdateParticipant(p.playerId, 'attending')}
                                                        title={t.eventScheduler.markAttending}
                                                    >
                                                        <Check size={12} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {event.participants?.length === 0 && (
                                <p style={{ color: '#6b7280', fontSize: '0.85rem', fontStyle: 'italic' }}>
                                    {t.eventScheduler.noRegistrations}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Admin Controls */}
                    {canDelete && (
                        <div className="admin-controls">
                            <button className="btn-danger" onClick={onDelete}>
                                <Trash2 size={16} /> {t.eventScheduler.deleteEvent}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Create Event Modal - simplified with mandatory date/time
const CreateEventModal = ({ user, games, onClose, onCreated }) => {
    const { t } = useI18n(user?.club?.language);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [gameId, setGameId] = useState('');
    const [minPlayers, setMinPlayers] = useState(2);
    const [maxPlayers, setMaxPlayers] = useState(6);
    const [eventDate, setEventDate] = useState('');
    const [eventTime, setEventTime] = useState('18:00');
    const [durationMinutes, setDurationMinutes] = useState(180);
    const [locationId, setLocationId] = useState('');
    const [locations, setLocations] = useState([]);
    const [suggestedLocation, setSuggestedLocation] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    
    // New location creation state
    const [showNewLocationForm, setShowNewLocationForm] = useState(false);
    const [newLocationName, setNewLocationName] = useState('');
    const [newLocationAddress, setNewLocationAddress] = useState('');
    const [savingLocation, setSavingLocation] = useState(false);
    
    // Fetch locations and suggested location on mount
    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const [locRes, suggestedRes] = await Promise.all([
                    apiFetch('/api/locations'),
                    apiFetch('/api/locations/suggested')
                ]);
                
                if (locRes.ok) {
                    const data = await locRes.json();
                    setLocations(data);
                }
                
                if (suggestedRes.ok) {
                    const data = await suggestedRes.json();
                    if (data.suggested) {
                        setSuggestedLocation(data.suggested);
                        setLocationId(data.suggested.id.toString());
                    }
                }
            } catch (err) {
                console.error('Fetch locations error:', err);
            }
        };
        fetchLocations();
    }, []);
    
    // Create a new location
    const handleCreateLocation = async () => {
        if (!newLocationName.trim()) {
            return;
        }
        
        setSavingLocation(true);
        try {
            const response = await apiFetch('/api/locations', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: newLocationName.trim(),
                    address: newLocationAddress.trim() || null
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                // Add new location to list and select it
                const newLoc = { id: data.id, name: data.name, address: data.address };
                setLocations(prev => [...prev, newLoc].sort((a, b) => a.name.localeCompare(b.name)));
                setLocationId(data.id.toString());
                // Reset form
                setNewLocationName('');
                setNewLocationAddress('');
                setShowNewLocationForm(false);
            } else {
                const errData = await response.json();
                setError(errData.error || t.eventScheduler.locationCreateFailed);
            }
        } catch (err) {
            console.error('Create location error:', err);
            setError(t.eventScheduler.locationCreateFailed);
        } finally {
            setSavingLocation(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (!title) {
            setError(t.eventScheduler.titleRequired);
            return;
        }
        
        if (!eventDate || !eventTime) {
            setError(t.eventScheduler.dateTimeRequired);
            return;
        }
        
        setSaving(true);
        try {
            const res = await apiFetch('/api/events', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    title,
                    description,
                    gameId: gameId || null,
                    createdBy: user.id,
                    minPlayers,
                    maxPlayers,
                    eventDate,
                    eventTime,
                    durationMinutes,
                    locationId: locationId || null
                })
            });
            
            if (res.ok) {
                onCreated();
            } else {
                const data = await res.json();
                setError(data.error || t.eventScheduler.createFailed);
            }
        } catch (err) {
            console.error('Create event error:', err);
            setError(t.eventScheduler.createFailed);
        } finally {
            setSaving(false);
        }
    };

    // Get tomorrow's date as minimum
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const minDate = tomorrow.toISOString().split('T')[0];

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3><CalendarPlus size={20} /> {t.eventScheduler.createTitle}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>
                
                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        {error && (
                            <div style={{
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid #ef4444',
                                borderRadius: '8px',
                                padding: '0.75rem',
                                marginBottom: '1rem',
                                color: '#ef4444',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <AlertCircle size={16} />
                                {error}
                            </div>
                        )}
                        
                        <div className="form-group">
                            <label>{t.eventScheduler.titleLabel}</label>
                            <input 
                                type="text" 
                                value={title} 
                                onChange={e => setTitle(e.target.value)}
                                placeholder={t.eventScheduler.titlePlaceholder}
                                required
                            />
                        </div>
                        
                        {/* Date & Time - Mandatory, prominently styled */}
                        <div style={{ 
                            background: 'rgba(96, 165, 250, 0.1)', 
                            border: '1px solid rgba(96, 165, 250, 0.3)',
                            borderRadius: '8px',
                            padding: '1rem',
                            marginBottom: '1rem'
                        }}>
                            <label style={{ 
                                color: '#60a5fa', 
                                fontSize: '0.9rem', 
                                marginBottom: '0.75rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <Clock size={16} /> {t.eventScheduler.scheduleLabel}
                            </label>
                            <div className="form-row">
                                <div className="form-group" style={{ marginBottom: 0 }}>
                                    <label style={{ fontSize: '0.8rem', color: '#9ca3af' }}>{t.eventScheduler.dateLabel}</label>
                                    <input 
                                        type="date" 
                                        value={eventDate} 
                                        onChange={e => setEventDate(e.target.value)}
                                        min={minDate}
                                        required
                                    />
                                </div>
                                <div className="form-group" style={{ marginBottom: 0 }}>
                                    <label style={{ fontSize: '0.8rem', color: '#9ca3af' }}>{t.eventScheduler.timeLabel}</label>
                                    <input 
                                        type="time" 
                                        value={eventTime} 
                                        onChange={e => setEventTime(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="form-group" style={{ marginBottom: 0, marginTop: '0.75rem' }}>
                                <label style={{ fontSize: '0.8rem', color: '#9ca3af' }}>{t.eventScheduler.estimatedDuration}</label>
                                <select value={durationMinutes} onChange={e => setDurationMinutes(parseInt(e.target.value))}>
                                    <option value={60}>{formatMemberText(t.eventScheduler.hour, { n: 1 })}</option>
                                    <option value={120}>{formatMemberText(t.eventScheduler.hours, { n: 2 })}</option>
                                    <option value={180}>{formatMemberText(t.eventScheduler.hours, { n: 3 })}</option>
                                    <option value={240}>{formatMemberText(t.eventScheduler.hours, { n: 4 })}</option>
                                    <option value={300}>{formatMemberText(t.eventScheduler.hours, { n: 5 })}</option>
                                    <option value={360}>{formatMemberText(t.eventScheduler.hours, { n: 6 })}</option>
                                    <option value={420}>{formatMemberText(t.eventScheduler.hours, { n: 7 })}</option>
                                    <option value={480}>{formatMemberText(t.eventScheduler.hours, { n: 8 })}</option>
                                    <option value={600}>{formatMemberText(t.eventScheduler.hours, { n: 10 })}</option>
                                    <option value={720}>{formatMemberText(t.eventScheduler.hours, { n: 12 })}</option>
                                    <option value={960}>{formatMemberText(t.eventScheduler.hours, { n: 16 })}</option>
                                    <option value={1200}>{formatMemberText(t.eventScheduler.hours, { n: 20 })}</option>
                                    <option value={1440}>{formatMemberText(t.eventScheduler.hours, { n: 24 })}</option>
                                </select>
                            </div>
                        </div>
                        
                        <div className="form-group">
                            <label>{t.eventScheduler.descriptionLabel}</label>
                            <textarea 
                                value={description} 
                                onChange={e => setDescription(e.target.value)}
                                placeholder={t.eventScheduler.descriptionPlaceholder}
                                rows={3}
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>{t.eventScheduler.gameOptional}</label>
                            <select value={gameId} onChange={e => setGameId(e.target.value)}>
                                <option value="">{t.eventScheduler.selectLater}</option>
                                {games?.map(g => (
                                    <option key={g.id} value={g.id}>{g.name}</option>
                                ))}
                            </select>
                        </div>
                        
                        {/* Location Selection */}
                        <div style={{ 
                            background: 'rgba(168, 85, 247, 0.1)', 
                            border: '1px solid rgba(168, 85, 247, 0.3)',
                            borderRadius: '8px',
                            padding: '1rem',
                            marginBottom: '1rem'
                        }}>
                            <label style={{ 
                                color: '#a855f7', 
                                fontSize: '0.9rem', 
                                marginBottom: '0.75rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <MapPin size={16} /> {t.eventScheduler.location}
                                </span>
                                {!showNewLocationForm && (
                                    <button
                                        type="button"
                                        onClick={() => setShowNewLocationForm(true)}
                                        style={{
                                            background: 'rgba(168, 85, 247, 0.2)',
                                            border: '1px solid rgba(168, 85, 247, 0.4)',
                                            borderRadius: '4px',
                                            padding: '0.25rem 0.5rem',
                                            color: '#a855f7',
                                            fontSize: '0.75rem',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.25rem'
                                        }}
                                    >
                                        <Plus size={12} /> {t.eventScheduler.addNewLocation}
                                    </button>
                                )}
                            </label>
                            
                            {/* New location form */}
                            {showNewLocationForm ? (
                                <div style={{ 
                                    background: 'rgba(168, 85, 247, 0.15)',
                                    borderRadius: '6px',
                                    padding: '0.75rem',
                                    marginBottom: '0.75rem'
                                }}>
                                    <div style={{ marginBottom: '0.5rem' }}>
                                        <label style={{ fontSize: '0.75rem', color: '#9ca3af', display: 'block', marginBottom: '0.25rem' }}>
                                            {t.eventScheduler.locationName}
                                        </label>
                                        <input
                                            type="text"
                                            value={newLocationName}
                                            onChange={e => setNewLocationName(e.target.value)}
                                            placeholder={t.eventScheduler.locationNamePlaceholder}
                                            style={{ width: '100%', fontSize: '0.9rem' }}
                                        />
                                    </div>
                                    <div style={{ marginBottom: '0.75rem' }}>
                                        <label style={{ fontSize: '0.75rem', color: '#9ca3af', display: 'block', marginBottom: '0.25rem' }}>
                                            {t.eventScheduler.addressOptional}
                                        </label>
                                        <input
                                            type="text"
                                            value={newLocationAddress}
                                            onChange={e => setNewLocationAddress(e.target.value)}
                                            placeholder={t.eventScheduler.addressPlaceholder}
                                            style={{ width: '100%', fontSize: '0.9rem' }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowNewLocationForm(false);
                                                setNewLocationName('');
                                                setNewLocationAddress('');
                                            }}
                                            style={{
                                                background: 'transparent',
                                                border: '1px solid rgba(156, 163, 175, 0.4)',
                                                borderRadius: '4px',
                                                padding: '0.4rem 0.75rem',
                                                color: '#9ca3af',
                                                fontSize: '0.8rem',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {t.common.cancel}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleCreateLocation}
                                            disabled={!newLocationName.trim() || savingLocation}
                                            style={{
                                                background: '#a855f7',
                                                border: 'none',
                                                borderRadius: '4px',
                                                padding: '0.4rem 0.75rem',
                                                color: 'white',
                                                fontSize: '0.8rem',
                                                cursor: newLocationName.trim() && !savingLocation ? 'pointer' : 'not-allowed',
                                                opacity: newLocationName.trim() && !savingLocation ? 1 : 0.5,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.25rem'
                                            }}
                                        >
                                            {savingLocation ? (
                                                <><Loader2 size={12} className="spin" /> {t.common.creating}</>
                                            ) : (
                                                <><Check size={12} /> {t.eventScheduler.createLocation}</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <select 
                                        value={locationId} 
                                        onChange={e => setLocationId(e.target.value)}
                                        style={{ width: '100%' }}
                                    >
                                        <option value="">{t.eventScheduler.noLocationSelected}</option>
                                        {locations.map(loc => (
                                            <option key={loc.id} value={loc.id}>
                                                {loc.name}{loc.address ? ` (${loc.address})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                    {locations.length === 0 && (
                                        <div style={{ 
                                            marginTop: '0.5rem', 
                                            fontSize: '0.8rem', 
                                            color: '#9ca3af',
                                            fontStyle: 'italic'
                                        }}>
                                            {t.eventScheduler.noSavedLocations}
                                        </div>
                                    )}
                                    {suggestedLocation && locationId === suggestedLocation.id.toString() && (
                                        <div style={{ 
                                            marginTop: '0.5rem', 
                                            fontSize: '0.8rem', 
                                            color: '#9ca3af',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.25rem'
                                        }}>
                                            {t.eventScheduler.suggestedLocation}
                                        </div>
                                    )}
                                    {locationId && locations.find(l => l.id.toString() === locationId)?.address && (
                                        <div style={{ 
                                            marginTop: '0.5rem', 
                                            fontSize: '0.85rem', 
                                            color: '#c9d1d9'
                                        }}>
                                            📍 {locations.find(l => l.id.toString() === locationId)?.address}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        
                        <div className="form-row">
                            <div className="form-group">
                                <label>{t.eventScheduler.minPlayers}</label>
                                <input 
                                    type="number" 
                                    value={minPlayers} 
                                    onChange={e => setMinPlayers(Math.max(1, Math.min(48, parseInt(e.target.value) || 1)))}
                                    min={1}
                                    max={48}
                                />
                            </div>
                            <div className="form-group">
                                <label>{t.eventScheduler.maxPlayers}</label>
                                <input 
                                    type="number" 
                                    value={maxPlayers} 
                                    onChange={e => setMaxPlayers(Math.max(1, Math.min(48, parseInt(e.target.value) || 1)))}
                                    min={1}
                                    max={48}
                                />
                            </div>
                        </div>
                        
                        <p style={{ 
                            color: '#6b7280', 
                            fontSize: '0.8rem', 
                            fontStyle: 'italic'
                        }}>
                            {t.eventScheduler.visibilityHint}
                        </p>
                    </div>
                    
                    <div className="modal-footer">
                        <button type="button" className="btn-secondary" onClick={onClose}>
                            {t.common.cancel}
                        </button>
                        <button type="submit" className="btn-primary" disabled={saving}>
                            {saving ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
                            {t.eventScheduler.createEvent}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EventScheduler;
